<template>
  <dropdown
    :auto-hide="autoHide"
    :boundary="boundary"
    :class="{ 'd-inline-flex': inline }"
    :delay="delay"
    :distance="distance"
    :placement="placement"
    :popper-class="vPopperClass"
    :shown="shown"
    :skidding="skidding"
    :triggers="triggers"
    @apply-hide="onApplyHide"
    @apply-show="onApplyShow"
    @auto-hide="onAutoHide"
    @close-directive="onCloseDirective"
    @close-group="onCloseGroup"
    @dispose="onDispose"
    @hide="onHide"
    @resize="onResize"
    @show="onShow"
    @update:open="onUpdateOpen"
  >
    <!--
      @slot This will be the popover target (for the events and position)
    -->
    <slot/>
    <template #popper>
      <h3
        v-if="heading"
        class="popover-header"
      >
        {{ heading }}
      </h3>
      <div class="popover-body">
        <!--
          @slot This will be the content of the popover
        -->
        <slot name="popover-content"/>
      </div>
    </template>
  </dropdown>
</template>

<script>
import { Dropdown } from 'floating-vue';

import { COLOR_WHITE, PLACEMENTS } from '../../config/constants';
import { POPOVER_COLORS } from './config';

export default {
  name: 'DpPopover',

  components: { Dropdown },

  props: {
    /**
     * Hide the popover if clicked outside. Especially useful when popover is triggered with click.
     * */
    autoHide: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * DOM element for the tooltip boundaries.
     * */
    boundary: {
      type: Object,
      required: false,
      default: undefined,
    },
    /**
     * Specifies popover color
     * @values white, gray-900
     * @default white
     * */
    color: {
      required: false,
      type: String,
      default: COLOR_WHITE,
      validator: (value) => POPOVER_COLORS.includes(value),
    },
    /**
     * Show/Hide delay, or object: { show: 250, hide: 250 } (ms).
     * */
    delay: {
      type: [String, Number, Object],
      required: false,
      default: () => ({ show: 0, hide: 0 }),
    },
    /**
     * Horizontal offset of the position (px).
     * */
    distance: {
      type: [String, Number],
      required: false,
      default: 5,
    },
    /**
     * Enables throwing events by the component
     * */
    enableEvents: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Title of the popover
     * */
    heading: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * Display the tooltip/popover trigger inline.
     * */
    inline: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * Boolean that shows or hide the tooltip/popover.
     */
    shown: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Class put on the tooltip/popover when it's open.
     */
    openClass: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * Classes applied to the popover element. Use this to apply different themes to the popover.
     * @default ''
     */
    popperClass: {
      type: [String, Array],
      required: false,
      default: 'popover',
    },
    /**
     * Tooltip position
     * @values top, bottom, left, right, auto, center
     */
    placement: {
      type: String,
      required: false,
      default: 'top',
      validator: (value) => PLACEMENTS.includes(value),
    },
    /**
     * Vertical offset of the position (px).
     * */
    skidding: {
      type: [String, Number],
      required: false,
      default: 0,
    },
    /**
     * Events triggering the tooltip separated with spaces:
     * @values hover, click, focus or empty array [] as manual.
     * */
    triggers: {
      type: Array,
      required: false,
      default: () => (['hover']),
    },
  },

  emits: [
    'update:open',
    'show',
    'apply-show',
    'hide',
    'apply-hide',
    'dispose',
    'auto-hide',
    'close-directive',
    'close-group',
    'resize',
  ],

  computed: {
    vPopperClass() {
      const classes = [];
      const { color, popperClass } = this;

      if (Array.isArray(popperClass) && popperClass.length > 0) {
        classes.push(...popperClass);
      }

      if (typeof popperClass === 'string') {
        classes.push(popperClass);
      }

      if (color) {
        classes.push(`popover-${color}`);
      }

      return classes;
    },
  },

  methods: {
    onUpdateOpen(value) {
      if (!this.enableEvents) {
        return;
      }

      /**
       * This allows you to use the .sync modifier on the open prop.
       * @type {boolean}
       */
      this.$emit('update:open', value);
    },

    onShow() {
      if (!this.enableEvents) {
        return;
      }

      /**
       * Emitted after popover is shown.
       */
      this.$emit('show');
    },

    onApplyShow() {
      if (!this.enableEvents) {
        return;
      }

      /**
       * Emitted after the show delay
       */
      this.$emit('apply-show');
    },
    onHide() {
      if (!this.enableEvents) {
        return;
      }

      /**
       * Emitted after popover is hidden.
       */
      this.$emit('hide');
    },

    onApplyHide() {
      if (!this.enableEvents) {
        return;
      }

      /**
       * Emitted after the hide delay
       */
      this.$emit('apply-hide');
    },

    onDispose() {
      if (!this.enableEvents) {
        return;
      }

      /**
       * Emitted before the component is unmounted (triggered in beforeUnmount lifecycle hook.)
       */
      this.$emit('dispose');
    },

    onAutoHide() {
      if (!this.enableEvents) {
        return;
      }

      /**
       * Emitted when the popover is closed if clicked outside.
       */
      this.$emit('auto-hide');
    },

    onCloseDirective() {
      if (!this.enableEvents) {
        return;
      }

      /**
       * Emitted when the popover is closed with the Close directive.
       */
      this.$emit('close-directive');
    },

    onCloseGroup() {
      if (!this.enableEvents) {
        return;
      }

      /**
       * Emitted when the popover is closed because a popover of another open-group was shown.
       */
      this.$emit('close-group');
    },

    onResize() {
      if (!this.enableEvents) {
        return;
      }

      /**
       * Emitted when the content size changes. You must set the handleResize prop to true.
       */
      this.$emit('resize');
    },
  },
};
</script>
